import { api } from '~/trpc-extra/api';
import type { TestContentMutationContext } from '../callback-handles';
import { cancelTestContentQueries, handleMutationError, handleMutationSuccess } from '../callback-handles';
import { isOperationOptimistic, OPTIMISTIC_STEP_KEY } from '../optimistic-handles';

export const useDuplicateStep = ({ testContentId }: { testContentId: string }) => {
  const trpcUtils = api.useUtils();

  const duplicateStepMutation = api.testStep.duplicateStep.useMutation<TestContentMutationContext>({
    onSuccess: () => handleMutationSuccess({ trpcUtils, testContentId }),
    onError: (_error, _variables, context) =>
      handleMutationError({ trpcUtils, testContentId, message: 'Error unable to duplicate step', context }),
    onMutate: async ({ stepId }) => {
      await cancelTestContentQueries({ trpcUtils, testContentId });
      const prevData = trpcUtils.testContent.get.getData({ id: testContentId });
      if (!prevData) {
        return { prevData: null };
      }

      const targetStep = prevData.steps.find((step) => step.id === stepId);
      if (!targetStep) {
        return { prevData };
      }

      const newData = {
        ...prevData,
        steps: [
          // Shift the order of the steps after the duplicated step
          ...prevData.steps.map((step) =>
            step.stepOrder > targetStep.stepOrder ? { ...step, stepOrder: step.stepOrder + 1 } : step,
          ),
          // Add the duplicated step
          {
            ...targetStep,
            id: `${OPTIMISTIC_STEP_KEY}-${targetStep.id}-${Date.now()}`,
            stepOrder: targetStep.stepOrder + 1,
            // We need to filter out operations that are being duplicated still
            // and not yet saved to the database.
            // Also, we need to give each operation a new ID to avoid conflicts with selected operations
            // and to ensure that the operations are unique
            operations:
              targetStep.operations
                ?.filter((operation) => !isOperationOptimistic(operation.id))
                ?.map((operation) => ({ ...operation, id: `${OPTIMISTIC_STEP_KEY}-${operation.id}-${Date.now()}` })) ??
              [],
          },
        ]
          // Sort the steps by order
          .sort((a, b) => a.stepOrder - b.stepOrder),
      };
      trpcUtils.testContent.get.setData({ id: testContentId }, newData);
      return { prevData };
    },
  });

  return duplicateStepMutation;
};
